import React, { useState } from 'react';
import type { Bookmark } from '@ext-bookmark/shared/types';
import { getFaviconUrl, getDirectFaviconUrl, getGoogleFaviconUrl, normalizeUrl } from '@ext-bookmark/shared/utils';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

interface BookmarkCardProps {
  bookmark: Bookmark;
  onEdit: (bookmark: Bookmark) => void;
  onDelete: (bookmarkId: string) => void;
}

const BookmarkCard: React.FC<BookmarkCardProps> = ({ bookmark, onEdit, onDelete }) => {
  const [showNote, setShowNote] = useState(false);
  const [clickStartPos, setClickStartPos] = useState<{ x: number; y: number } | null>(null);
  const [faviconSrc, setFaviconSrc] = useState<string>(getFaviconUrl(bookmark.url));
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const [showFallback, setShowFallback] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    setClickStartPos({ x: e.clientX, y: e.clientY });
  };

  const handleClick = (e: React.MouseEvent) => {
    // 检查是否是真正的点击（没有移动太多）
    if (clickStartPos) {
      const deltaX = Math.abs(e.clientX - clickStartPos.x);
      const deltaY = Math.abs(e.clientY - clickStartPos.y);
      // 如果移动超过 5px，认为是拖拽，不触发点击
      if (deltaX > 5 || deltaY > 5) {
        return;
      }
    }
    const url = normalizeUrl(bookmark.url);
    window.open(url, '_blank');
    setClickStartPos(null);
  };

  const handleFaviconError = () => {
    // Fallback 策略：
    // 1. 如果当前是 Google 服务，尝试直接访问网站的 favicon.ico
    // 2. 如果当前是直接 favicon.ico，尝试 Google 服务（如果之前没试过）
    // 3. 如果都失败，显示首字母图标
    
    if (fallbackIndex === 0) {
      // 第一次失败：尝试直接访问网站的 favicon.ico
      const directUrl = getDirectFaviconUrl(bookmark.url);
      if (directUrl && directUrl !== faviconSrc) {
        setFaviconSrc(directUrl);
        setFallbackIndex(1);
      } else {
        // 如果直接 URL 不可用，尝试 Google 服务
        const googleUrl = getGoogleFaviconUrl(bookmark.url);
        if (googleUrl && googleUrl !== faviconSrc) {
          setFaviconSrc(googleUrl);
          setFallbackIndex(2);
        } else {
          setShowFallback(true);
        }
      }
    } else if (fallbackIndex === 1) {
      // 第二次失败：尝试 Google 服务
      const googleUrl = getGoogleFaviconUrl(bookmark.url);
      if (googleUrl && googleUrl !== faviconSrc) {
        setFaviconSrc(googleUrl);
        setFallbackIndex(2);
      } else {
        setShowFallback(true);
      }
    } else {
      // 第三次失败：显示首字母图标
      setShowFallback(true);
    }
  };

  return (
    <Card className="group relative hover:shadow-xl transition-all duration-200 cursor-pointer">
      <CardContent className="p-4">
        <div 
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          className="flex items-start gap-3"
        >
          {/* Favicon */}
          <div className="flex-shrink-0">
            {showFallback ? (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white bg-gradient-to-br from-blue-500 to-blue-600">
                {bookmark.name.charAt(0).toUpperCase()}
              </div>
            ) : (
              <img
                src={faviconSrc}
                alt=""
                className="w-10 h-10 rounded-xl"
                onError={handleFaviconError}
              />
            )}
          </div>

          {/* 内容 */}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {bookmark.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1">
              {bookmark.url}
            </div>
            {bookmark.note && (
              <div className="mt-2">
                {showNote ? (
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    {bookmark.note}
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowNote(true);
                    }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    显示备注
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 操作按钮 */}
          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(bookmark);
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('确定要删除这个书签吗？')) {
                  onDelete(bookmark.id);
                }
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BookmarkCard;
